import React, { useEffect, useState, useMemo } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useUserContext } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import debounce from 'lodash.debounce';
import { API_URL } from '../config/api';

export default function Dashboard() {
  const { user, clearUser } = useUserContext();
  const navigate = useNavigate();

  const [commandes, setCommandes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openDetailsId, setOpenDetailsId] = useState(null);
  const itemsPerPage = 20;

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  // 🔹 Fetch commandes avec prixAchat
  useEffect(() => {
    const fetchCommandes = async () => {
      try {
        const res = await fetch(`${API_URL}/commandes?adminId=${user?._id}`);
        const data = await res.json();

        const commandesAvecPrixAchat = data.map(commande => {
          const cart = commande.cart.map(item => ({
            ...item,
            prixAchat: item.productId?.prixAchat || 0,
            productTitle: item.productId?.title || item.title,
            productPrice: item.productId?.price || item.price
          }));
          return { ...commande, cart };
        });

        setCommandes(commandesAvecPrixAchat);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCommandes();
  }, [user]);

  // 🔹 Filtrage commandes livrées + recherche + date
  const filteredCommandes = useMemo(() => {
    return commandes.filter(c => {
      if (c.status !== 'livrée') return false;
      const clientName = (c.client?.name || c.name || '').toLowerCase();
      if (searchTerm && !clientName.includes(searchTerm.toLowerCase())) return false;

      const createdAt = new Date(c.createdAt);
      if (startDate && createdAt < new Date(startDate)) return false;
      if (endDate && createdAt > new Date(endDate + 'T23:59:59')) return false;

      return true;
    });
  }, [commandes, searchTerm, startDate, endDate]);

  const totalPages = Math.ceil(filteredCommandes.length / itemsPerPage);
  const currentPageCommandes = filteredCommandes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const chiffreAffaires = filteredCommandes.reduce((sum, c) => sum + (c.totalAmount || 0), 0);

  const margeTotale = useMemo(() => {
    return filteredCommandes.reduce((totalMarge, commande) => {
      if (!commande.cart) return totalMarge;
      const margeCommande = commande.cart.reduce((somme, item) => {
        const prixVente = Number(item.price) || 0;
        const prixAchat = Number(item.prixAchat) || 0;
        const quantite = Number(item.quantity) || 1;
        return somme + (prixVente - prixAchat) * quantite;
      }, 0);
      return totalMarge + margeCommande;
    }, 0);
  }, [filteredCommandes]);

  // 🔹 KPI du mois en cours
  const currentMonthStats = useMemo(() => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();

    const commandesMois = filteredCommandes.filter(c => {
      const d = new Date(c.createdAt);
      return d.getMonth() === month && d.getFullYear() === year;
    });

    const ca = commandesMois.reduce((sum, c) => sum + (c.totalAmount || 0), 0);
    const marge = commandesMois.reduce((sum, c) => {
      if (!c.cart) return sum;
      return sum + c.cart.reduce((s, item) => s + ((Number(item.price) - Number(item.prixAchat)) * (Number(item.quantity) || 1)), 0);
    }, 0);

    return {
      commandesCount: commandesMois.length,
      chiffreAffaires: ca,
      marge: marge
    };
  }, [filteredCommandes]);

  const logoutHandler = () => {
    clearUser();
    navigate('/login');
  };

  // 🔹 Export Excel
const handleExportExcel = () => {
  const wb = XLSX.utils.book_new();

  filteredCommandes.forEach(c => {
    const wsData = [
      ["Client", c.client?.name || c.name || 'Inconnu'],
      ["Adresse", c.client?.address || '-'],
      ["Téléphone", c.client?.number || '-'],
      ["Date", new Date(c.createdAt).toLocaleDateString()],
      ["Total", (c.totalAmount || 0) + ' FCFA'],
      ["Marge", c.cart?.reduce((sum, item) => sum + (Number(item.price || 0) - Number(item.prixAchat || 0)) * Number(item.quantity || 1), 0) + ' FCFA'],
      [],
      ["Produit", "Prix Vente", "Prix Achat", "Quantité", "Bénéfice"]
    ];

    c.cart.forEach(item => {
      wsData.push([
        item.productTitle,
        Number(item.price || 0) + ' FCFA',
        Number(item.prixAchat || 0) + ' FCFA',
        item.quantity || 1,
        ((Number(item.price || 0) - Number(item.prixAchat || 0)) * (item.quantity || 1)) + ' FCFA'
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, c.client?.name?.substring(0, 20) || 'Commande');
  });

  XLSX.writeFile(wb, "commandes_detail.xlsx");
};

  // 🔹 Export PDF
const handleExportPDF = () => {
  // ⚡ jsPDF en mode paysage et format A4
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4"
  });

  const tableColumn = ["Client", "Adresse", "Téléphone", "Date", "Total", "Marge", "Statut"];
  const tableRows = filteredCommandes.map(c => [
    c.client?.name || c.name || 'Inconnu',
    c.client?.address || '-',
    c.client?.number || '-',
    new Date(c.createdAt).toLocaleDateString(),
    (c.totalAmount || 0) + ' FCFA',
    c.cart?.reduce((sum, item) => sum + (Number(item.price || 0) - Number(item.prixAchat || 0)) * Number(item.quantity || 1), 0) + ' FCFA',
    c.status
  ]);

  // Définir l'en-tête et les pieds de page
  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 60, // espace pour l'en-tête
    theme: 'striped',
    styles: {
      fontSize: 10,
      cellPadding: 5,
      overflow: 'linebreak',
      cellWidth: 'auto'
    },
    headStyles: { fillColor: [108, 93, 211], textColor: 255 },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { left: 20, right: 20 },
    didDrawPage: (data) => {
      // En-tête
      doc.setFontSize(14);
      doc.setTextColor(40);
      doc.setFont(undefined, 'bold');
      doc.text("📦 CodeShop225 - Dashboard Commandes", data.settings.margin.left, 30);

      // Numéro de page
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
    }
  });

  doc.save("commandes.pdf");
};

 return (
    <div style={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Navbar logoutHandler={logoutHandler} />
      <main style={{ padding: '30px' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '20px', color: '#2c3e50' }}>📊 Dashboard Statistiques</h1>

        {/* Cartes statistiques globales */}
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px', flexWrap: 'wrap' }}>
          <Card title="Commandes Livrées" value={filteredCommandes.length} color="#6C5DD3" />
          <Card title="Chiffre d'affaires" value={chiffreAffaires.toLocaleString() + ' FCFA'} color="#00B8D9" />
          <Card title="Marge (Bénéfice)" value={margeTotale.toLocaleString() + ' FCFA'} color="#4CAF50" />
        </div>

        {/* KPI mois en cours */}
        <h2 style={{ textAlign: 'center', marginBottom: '15px', color: '#34495e' }}>📅 Statistiques du mois en cours</h2>
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px', flexWrap: 'wrap' }}>
          <Card title="Commandes" value={currentMonthStats.commandesCount} color="#6C5DD3" />
          <Card title="Chiffre d'affaires" value={currentMonthStats.chiffreAffaires.toLocaleString() + ' FCFA'} color="#00B8D9" />
          <Card title="Marge" value={currentMonthStats.marge.toLocaleString() + ' FCFA'} color="#4CAF50" />
        </div>

        {/* Filtres et export */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={filterInputStyle} />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={filterInputStyle} />
          <input type="text" placeholder="Rechercher un client..." onChange={debounce(e => setSearchTerm(e.target.value), 300)} style={filterInputStyle} />
          <button onClick={handleExportExcel} style={exportBtnStyle}>Exporter Excel</button>
          <button onClick={handleExportPDF} style={{ ...exportBtnStyle, backgroundColor: '#E53935' }}>Exporter PDF</button>
        </div>

        {/* Tableau */}
        <div style={{ overflowX: 'auto', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
          <table style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Client</th>
                <th>Date</th>
                <th>Montant</th>
                <th>Marge</th>
                <th>Détails</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {currentPageCommandes.map((row, i) => {
                const margeCommande = row.cart?.reduce((sum, item) => sum + (Number(item.price || 0) - Number(item.prixAchat || 0)) * Number(item.quantity || 1), 0);
                return (
                  <React.Fragment key={i}>
                    <tr style={tbodyRowStyle}>
                      <td>{row.client?.name || row.name || 'Inconnu'}</td>
                      <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                      <td>{Number(row.totalAmount || 0).toLocaleString()} FCFA</td>
                      <td>{margeCommande.toLocaleString()} FCFA</td>
                      <td>
                        <button onClick={() => setOpenDetailsId(openDetailsId === row._id ? null : row._id)} style={detailsBtnStyle}>
                          {openDetailsId === row._id ? 'Masquer' : 'Voir'}
                        </button>
                      </td>
                      <td>
                        <span style={{ padding: '4px 10px', borderRadius: '20px', backgroundColor: row.status === 'livrée' ? '#2ecc71' : '#e74c3c', color: '#fff', fontWeight: '500' }}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
      </main>
      <Footer />
    </div>
  );
}

// Pagination et Card
const Pagination = ({ currentPage, totalPages, setCurrentPage }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={paginationBtnStyle}>◀</button>
    <span style={{ lineHeight: '32px' }}>{currentPage} / {totalPages}</span>
    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} style={paginationBtnStyle}>▶</button>
  </div>
);

const Card = ({ title, value, color }) => (
  <div style={{ backgroundColor: color, padding: '20px 30px', borderRadius: '10px', color: '#fff', minWidth: '180px', boxShadow: '0 6px 12px rgba(0,0,0,0.15)', textAlign: 'center' }}>
    <h3 style={{ fontWeight: 500, marginBottom: '10px' }}>{title}</h3>
    <h2 style={{ fontSize: '1.6rem', fontWeight: 700 }}>{value}</h2>
  </div>
);

const filterInputStyle = { padding: '10px 15px', borderRadius: '8px', border: '1px solid #ccc', minWidth: '180px' };
const exportBtnStyle = { padding: '10px 20px', backgroundColor: '#00B8D9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' };
const detailsBtnStyle = { padding: '4px 10px', borderRadius: '6px', border: 'none', backgroundColor: '#6C5DD3', color: '#fff', cursor: 'pointer' };
const paginationBtnStyle = { padding: '6px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#6C5DD3', color: '#fff', cursor: 'pointer' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', minWidth: '700px' };
const theadStyle = { backgroundColor: '#f4f6f8', color: '#34495e', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #ddd' };
const tbodyRowStyle = { borderBottom: '1px solid #eee', padding: '12px', fontSize: '0.95rem' };